//React
import React, { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
interface InsufficientCreditsModalProps {
    id: string;
    creditsRequired: number;
    creditsAvailable: number;
    deficit: number;
    plan?: string;
    onClose?: () => void;
    isOwner?: boolean; // Indique si l'utilisateur est ADMIN (peut choisir un plan)
    adminEmail?: string; // Email de l'administrateur (pour les non-admin)
}

const InsufficientCreditsModal: React.FC<InsufficientCreditsModalProps> = ({
    id,
    creditsRequired,
    creditsAvailable,
    deficit,
    plan = "FREE",
    onClose,
    isOwner = false, // Par défaut, considérer comme non-admin
    adminEmail
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const handleChoosePlan = () => {
        if (closeRef.current) {
            closeRef.current.click();
        }
        if (onClose) {
            onClose();
        }
        navigate("/pricing");
    };

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    // Écouter l'événement de fermeture du modal Bootstrap
    useEffect(() => {
        const modalElement = modalRef.current;
        if (modalElement && onClose) {
            const handleModalHidden = () => {
                // Restaurer le overflow du body quand le modal se ferme
                document.body.style.overflow = '';
                handleClose();
            };
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                // Nettoyer au cas où le composant serait démonté
                document.body.style.overflow = '';
            };
        }
    }, [onClose, handleClose]);

    const modalContent = (
        <div 
            ref={modalRef}
            className="modal fade" 
            data-bs-backdrop="true" 
            data-bs-keyboard="true"
            id={id} 
            tabIndex={-1}
            aria-labelledby={`${id}Label`}
            aria-hidden="true"
            style={{ zIndex: 10000, position: 'fixed' }}
        >
            <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 10001, pointerEvents: 'auto', position: 'relative' }}>
                <div className="modal-content bg-dark" style={{ zIndex: 10002, pointerEvents: 'auto', position: 'relative' }}>
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white" id={`${id}Label`}>
                            🔒 {t('views.modal.insufficientCredits.title', 'Crédits insuffisants')}
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close bg-white" 
                            data-bs-dismiss="modal" 
                            aria-label="Close"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="text-theme">
                            <p className="mb-3">
                                {t('views.modal.insufficientCredits.message', 'Cette action nécessite des crédits supplémentaires.')}
                            </p>
                            <div className="mb-3 p-3 bg-dark border border-secondary rounded">
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{t('views.modal.insufficientCredits.creditsRequired', 'Crédits requis')}:</span>
                                    <strong className="text-warning">{creditsRequired}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{t('views.modal.insufficientCredits.creditsAvailable', 'Crédits disponibles')}:</span>
                                    <strong className="text-info">{creditsAvailable}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>{t('views.modal.insufficientCredits.deficit', 'Déficit')}:</span>
                                    <strong className="text-danger">{deficit}</strong>
                                </div>
                            </div>
                            {isOwner ? (
                                <p className="mb-2">
                                     {t('views.modal.insufficientCredits.suggestion', 'Choisissez un plan payant pour débloquer cette fonctionnalités')}
                                </p>
                            ) : (
                                <>
                                    <p className="mb-2">
                                         {t('views.modal.insufficientCredits.contactOwner', 'Contactez l\'administrateur de votre compagnie pour mettre à niveau le plan et obtenir plus de crédits.')}
                                    </p>
                                    {adminEmail && (
                                        <p className="mb-2 mt-2">
                                            <span className="d-block text-muted">
                                                {t('views.modal.insufficientCredits.ownerEmail', 'Administrateur')}: <strong className="text-white">{adminEmail}</strong>
                                            </span>
                                        </p>
                                    )}
                                </>
                            )}
                            
                        </div>
                    </div>
                    <div className="modal-footer border-0">
                        <button 
                            ref={closeRef}
                            type="button" 
                            className="btn btn-secondary" 
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                            {t('words.close', 'Fermer')}
                        </button>
                        {isOwner && (
                            <button 
                                type="button" 
                                onClick={handleChoosePlan}
                                className="btn btn-primary"
                                style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                            >
                                {t('views.modal.insufficientCredits.choosePlan', 'Choisir un plan')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // Utiliser un portail pour rendre le modal au niveau racine du document
    return createPortal(modalContent, document.body);
};

export default InsufficientCreditsModal;

